import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { COMPANY_COLORS } from '@/constants/colors.constant'
import { useAttributionPeriodLabel, useAttributionTotalReturns } from '@/contexts/FAAttributionDataContext/hooks'
import { useIsAnalysisLoading } from '@/contexts/RADashboardDataContext/hooks'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { CardProps } from '@/interfaces/raDashboard.interface'
import { formatReturnValue } from '@/utils/attributionReturnFormat'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

// Which total-return figure the card shows (like DataPointCard's dataPoint parameter).
export type AttributionReturnMetric = 'Portfolio' | 'Benchmark' | 'Active'

export type AttributionReturnCardProps = CardProps & {
  metric: AttributionReturnMetric
}

const POSITIVE_COLOR = COMPANY_COLORS.SUCCESS
const NEGATIVE_COLOR = COMPANY_COLORS.ERROR

// Attribution return card. Mirrors the risk DataPointCard chrome: title header with a
// divider, the period under it, the value centered, and the description as a footer.
const AttributionReturnCard: React.FC<AttributionReturnCardProps> = ({ cardId, metric }) => {
  const theme = useTheme()
  const isLoading = useIsAnalysisLoading()
  const totalReturn = useAttributionTotalReturns()
  const periodLabel = useAttributionPeriodLabel()
  const title = CardTitleLookup[cardId]

  const value =
    totalReturn == null
      ? null
      : metric === 'Portfolio'
        ? totalReturn.portfolioContribution
        : metric === 'Benchmark'
          ? totalReturn.benchmarkContribution
          : totalReturn.differenceContribution
  // Active return also notes out/under-performance vs the benchmark.
  const description =
    metric === 'Active' && value != null
      ? value >= 0
        ? 'Outperformance vs benchmark'
        : 'Underperformance vs benchmark'
      : undefined

  const { text: valueText, sign } = formatReturnValue(value, isLoading)
  const valueColor = sign === 'none' ? theme.textSecondary : sign === 'positive' ? POSITIVE_COLOR : NEGATIVE_COLOR

  return (
    <Stack
      sx={{
        ...cardStyle,
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'visible',
        padding: '12px',
        boxSizing: 'border-box',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack sx={{ borderBottom: `1px solid ${theme.border}` }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: theme.textPrimary,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
      </Stack>
      {periodLabel && (
        <Typography sx={{ fontSize: '12px', color: theme.textSecondary, mt: '4px' }}>
          {periodLabel}
        </Typography>
      )}
      <Stack
        sx={{
          textAlign: 'center',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: valueColor }}>
          {valueText}
        </Typography>
        {description && (
          <Typography sx={{ fontSize: '14px', color: theme.textSecondary }}>
            {description}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

export default AttributionReturnCard
